import styled, { css } from 'styled-components';
import { SubscriptionType } from '../api/types';
import { formatNumberToCurrency } from '../../../utils';
import { CheckIcon } from '../../../assets/icons/check';
import { ColorKeys } from '../../../utils/theme';

type SubscriptionCardProps = {
  subscription: SubscriptionType;
  isCurrentPlan?: boolean;
  onButtonPress: (newPlan: SubscriptionType) => void;
};

export const SubscriptionCard = ({
  subscription,
  isCurrentPlan,
  onButtonPress
}: SubscriptionCardProps) => {
  const isRecurring = Boolean(subscription.recurring);

  const getIncludeText = () => {
    const entries = Object.entries(subscription.metadata);

    const arrComponent = [];

    for (const [key, value] of entries) {
      if (key === 'GB') {
        const component = (
          <IncludesSection key="gb">
            <CheckWrapper>
              <CheckIcon size={12} color="white" />
            </CheckWrapper>
            <p>{value}GB data per month</p>
          </IncludesSection>
        );
        arrComponent.push(component);
      }

      if (key === 'Provider') {
        const component = (
          <IncludesSection key="provider">
            <CheckWrapper>
              <CheckIcon size={12} color="white" />
            </CheckWrapper>
            <p>Multi Network:</p>
            <p>{value}</p>
          </IncludesSection>
        );

        arrComponent.push(component);
      }

      if (key === 'Hours') {
        // For Solar PTZ plans, display "per year" instead of "per month"
        const isSolarPTZ = subscription.product_name?.startsWith('Solar PTZ');
        const timeframe = isSolarPTZ ? 'year' : 'month';
        const component = (
          <IncludesSection key="hours">
            <CheckWrapper>
              <CheckIcon size={12} color="white" />
            </CheckWrapper>
            <p>{value} hours of streaming per {timeframe}</p>
          </IncludesSection>
        );
        arrComponent.push(component);
      }
    }

    // Add livestreaming feature for Heavy Streaming plans
    if (subscription.product_name === 'Heavy Streaming') {
      const livestreamComponent = (
        <IncludesSection key="livestream">
          <CheckWrapper>
            <CheckIcon size={12} color="white" />
          </CheckWrapper>
          <p>Livestream directly to your website (NEW FEATURE!)</p>
        </IncludesSection>
      );
      arrComponent.push(livestreamComponent);
    }

    return arrComponent;
  };

  const cardColor = (): ColorKeys => {
    if (isCurrentPlan) {
      return 'primary100';
    }
    if (subscription.upgrade_data) {
      return 'green900';
    }
    if (subscription.upgrade_value) {
      return 'green900';
    }
    return 'black';
  };

  const getBadge = () => {
    if (isCurrentPlan) {
      return <CurrentPlanBadge>Current plan</CurrentPlanBadge>;
    }

    if (subscription.upgrade_data) {
      return <CurrentPlanBadge color={cardColor()}>Upgrade for more data</CurrentPlanBadge>;
    }

    if (subscription.upgrade_value) {
      return <CurrentPlanBadge color={cardColor()}>Upgrade for better value</CurrentPlanBadge>;
    }
  };
  return (
    <Container isCurrentPlan={isCurrentPlan} color={cardColor()}>
      {getBadge()}
      <Title>{subscription.product_name}</Title>
      <MiddleWrapper>
        <PriceWrapper>
          <Price>
            {formatNumberToCurrency(subscription.unit_amount / 100, subscription.currency)}
          </Price>
          {isRecurring && <Recurring>/{subscription.recurring?.interval}</Recurring>}
        </PriceWrapper>
        {Boolean(subscription.amountSaved) && (
          <SaveBadge>
            Saving {formatNumberToCurrency(subscription.amountSaved, subscription.currency)}
          </SaveBadge>
        )}
      </MiddleWrapper>
      <IncludesContainer>
        <p>This plan includes</p>
        {getIncludeText()}
      </IncludesContainer>
      {subscription.subscribed ? (
        <CurrentPlanFooter>✓ You're on this plan</CurrentPlanFooter>
      ) : (
        <SubscribeBtn onClick={() => onButtonPress(subscription)}>Switch plan</SubscribeBtn>
      )}
    </Container>
  );
};

const CurrentPlanFooter = styled.div`
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.primary}40;
  background: ${({ theme }) => theme.colors.primary}10;
  border-radius: 0.75rem;
  width: 100%;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.small};
  text-align: center;
`;

const MiddleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SaveBadge = styled.div`
  border-radius: 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.green900}, ${({ theme }) => theme.colors.green900}90);
  color: white;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const IncludesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.small};
`;

const CheckWrapper = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colors.primary};
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

const IncludesSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.3rem;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
`;

const Title = styled.p`
  font-size: ${({ theme }) => theme.fontSize.medium};
  font-weight: bold;
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Recurring = styled.span`
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray500};
  margin-left: 0.3rem;
  margin-top: 0.5rem;
`;

const Price = styled.p`
  font-size: ${({ theme }) => theme.fontSize.large};
  font-weight: bold;
`;

const Container = styled.div<{ isCurrentPlan?: boolean }>`
  padding: 1rem;
  width: 100%;
  background: ${({ theme, isCurrentPlan }) => 
    isCurrentPlan 
      ? `linear-gradient(135deg, ${theme.colors.primary}08, ${theme.colors.primary}15)`
      : `linear-gradient(135deg, ${theme.colors.white}, ${theme.colors.gray}15)`
  };
  border: 1px solid ${({ theme, isCurrentPlan }) => 
    isCurrentPlan 
      ? `${theme.colors.primary}30` 
      : `${theme.colors.gray500}20`
  };
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  justify-content: space-between;
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;
  margin-top: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    border-color: ${({ theme }) => theme.colors.primary}30;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme, isCurrentPlan }) => 
      isCurrentPlan 
        ? `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.primary}70)`
        : 'transparent'
    };
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}70);
    opacity: 1;
  }
`;

const SubscribeBtn = styled.button`
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}90);
  border: none;
  border-radius: 0.75rem;
  width: 100%;
  color: white;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.small};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}90, ${({ theme }) => theme.colors.primary});
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CurrentPlanBadge = styled.div<{ color?: ColorKeys }>`
  display: flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  background: linear-gradient(135deg, ${({ theme, color = 'primary' }) => theme.colors[color]}, ${({ theme, color = 'primary' }) => theme.colors[color]}90);
  color: white;
  border-radius: 1.25rem;
  position: absolute;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  top: -0.75rem;
  right: 1rem;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  border: 2px solid white;
`;
